import * as React from 'react';

import { isPresent, Nullable } from '@common/typescript/objects/Nullable';

import { DeliveryType } from '@app/objects/Pet';
import { useCrematoryFeatures } from '@app/hooks/Crematory/useCrematoryFeatures';
import { DeliveryTypeEntity, SystemDeliveryType } from '@app/objects/DeliveryType';
import { useDeliveryTypes } from '@app/hooks/Delivery/useDeliveryTypes';

type TypeTester = (value: Nullable<string | number>) => boolean;

export interface DeliveryManager {
	type: 'modern' | 'obsolete';

	isHomeDropOff: TypeTester;
	isVetDropOff: TypeTester;
	isClinic: TypeTester;
	isMail: TypeTester;
	isPickup: TypeTester;
	isNone: TypeTester;
	is: (value: Nullable<string | number>, oneOf: Array<DeliveryType>) => boolean;
}

/**
 * Convert numeric value (or string-encoded number) to Delivery Type
 * @param {Nullable<string | number>} value value to be converted into DeliveryType
 * @returns {Nullable<DeliveryType>} DeliveryType or null if conversion is not possible
 */
function toDeliveryType(value: Nullable<string | number>): Nullable<DeliveryType> {
	if (value === null) return null;

	const num = Number(value);
	if (Number.isNaN(num)) return null;

	return num as DeliveryType;
}

/**
 * Check if value is equal to ref value
 * @param {Nullable<string | number>} value to be tested
 * @param {DeliveryType} ref value to test against
 * @returns {boolean} true if equal / false otherwise
 */
function isDeliveryType(value: Nullable<string | number>, ref: DeliveryType): boolean {
	const type = toDeliveryType(value);

	return type === ref;
}

const obsoleteManager: DeliveryManager = {
	type: 'obsolete',

	isHomeDropOff: (value: Nullable<string | number>) => isDeliveryType(value, DeliveryType.HomeDropOff),
	isVetDropOff: (value: Nullable<string | number>) => isDeliveryType(value, DeliveryType.VetDropOff),
	isClinic: (value: Nullable<string | number>) => isDeliveryType(value, DeliveryType.Clinic),
	isMail: (value: Nullable<string | number>) => isDeliveryType(value, DeliveryType.Mail),
	isPickup: (value: Nullable<string | number>) => isDeliveryType(value, DeliveryType.Pickup),
	isNone: (value: Nullable<string | number>) => isDeliveryType(value, DeliveryType.None),
	is: (value: Nullable<string | number>, oneOf: Array<DeliveryType>) => {
		const cast = toDeliveryType(value);
		if (cast === null) return false;

		return oneOf.includes(cast);
	},
};

/**
 * Convert SystemDeliveryType to corresponding DeliveryType enum
 * Conversions is done by underline function that DeliveryType represents
 * @param {DeliveryType} deliveryType Delivery Type to be converted to SystemDeliveryType (if possible)
 * @returns {Nullable<SystemDeliveryType>} SystemDeliveryType or null if no corresponding SystemDeliveryType found
 */
function toSystemType(deliveryType: DeliveryType): Nullable<SystemDeliveryType> {
	switch (deliveryType) {
	case DeliveryType.Clinic: return SystemDeliveryType.Clinic;
	case DeliveryType.Mail: return SystemDeliveryType.Mail;
	case DeliveryType.HomeDropOff: return SystemDeliveryType.HomeDropOff;
	case DeliveryType.Pickup: return SystemDeliveryType.Pickup;

	default: return null;
	}
}

class ModernDeliveryManager implements DeliveryManager {
	private readonly _store: Map<number, DeliveryTypeEntity>;

	constructor(store: Map<number, DeliveryTypeEntity>) {
		this._store = store;
	}

	private getDeliveryType(id: string | number): DeliveryTypeEntity | undefined {
		const num = Number(id);
		if (Number.isNaN(num)) return undefined;

		return this._store.get(num);
	}

	private isSystemType(id: Nullable<string | number>, type: Nullable<SystemDeliveryType>): boolean {
		if (id === null) return false;

		const value = this.getDeliveryType(id);
		if (!value) return false;

		return value.systemType === type;
	}

	public get type(): 'modern' {
		return 'modern';
	}

	public isHomeDropOff(value: Nullable<string | number>): boolean {
		return this.isSystemType(value, SystemDeliveryType.HomeDropOff);
	}

	public isVetDropOff(): boolean {
		return false;
	}

	public isClinic(value: Nullable<string | number>): boolean {
		return this.isSystemType(value, SystemDeliveryType.Clinic);
	}

	public isMail(value: Nullable<string | number>): boolean {
		return this.isSystemType(value, SystemDeliveryType.Mail);
	}

	public isPickup(value: Nullable<string | number>): boolean {
		return this.isSystemType(value, SystemDeliveryType.Pickup);
	}

	public isNone(value: Nullable<string | number>): boolean {
		return value === null || this.isSystemType(value, null);
	}

	public is(value: Nullable<string | number>, oneOf: Array<DeliveryType>): boolean {
		if (value === null) return oneOf.includes(DeliveryType.None);

		const delivery = this.getDeliveryType(value);

		if (!delivery) return false;
		if (delivery.systemType === null) return false;

		return oneOf.map(toSystemType).filter(isPresent).includes(delivery.systemType);
	}
}

export function useDeliveryManager(): DeliveryManager {
	const features = useCrematoryFeatures();
	const types = useDeliveryTypes();
	const manager = React.useMemo(() => new ModernDeliveryManager(types.map), [types.map]);

	if (features.item?.enableCustomDeliveryType) return manager;

	return obsoleteManager;
}
